import type { GetWorkspacesBasicQuery } from '@frameio/graphql-client/generated';
import { useGetAccountInfoForWorkspacesQuery } from '@frameio/graphql-client/generated';
import {
  useActiveAccountId,
  useDispatchAppState,
} from '@frameio/shared/contexts';
import { useSheetContext } from '@frameio/vapor';
import {
  useGetWorkspacesInfo,
  useInvitedToProjects,
  usePaginatedWorkspacesLegacy,
  useWorkspaceIds,
} from '../../hooks';
import { WorkspacesNav } from './WorkspacesNav';

/**
 * Workspaces Nav that can be rendered independently (i.e. outside of )
 * mobile view
 */
export const WorkspacesNavStandalone = () => {
  const { onRequestClose: closeMobileNavSheet } = useSheetContext();
  const dispatch = useDispatchAppState();
  const accountId = useActiveAccountId();

  const {
    data: accountInfoData,
    error: accountInfoError,
    loading: accountInfoLoading,
  } = useGetAccountInfoForWorkspacesQuery({
    variables: {
      accountId,
    },
    skip: !accountId,
  });

  const {
    data: workspacesData,
    loading: workspacesLoading,
    error: workspacesError,
    fetchNext,
  } = useGetWorkspacesInfo({
    accountId,
  });

  const { handleFetchNext, hasNextPage } = usePaginatedWorkspacesLegacy({
    data: workspacesData as GetWorkspacesBasicQuery,
    loading: workspacesLoading,
    fetchNext,
  });

  const loading = accountInfoLoading || workspacesLoading;
  const error = accountInfoError || workspacesError;

  const workspaceIds = useWorkspaceIds({ accountId });
  const { invitedToProjectIds } = useInvitedToProjects({ accountId });

  const canCreateWorkspaces =
    accountInfoData?.account?.viewerAccountData?.canCreateWorkspace;
  const accountTier = accountInfoData?.account?.tier;

  if (!accountInfoData || !workspacesData) return null;

  const onWorkspaceSelected = (workspaceId: string) => {
    closeMobileNavSheet();
    dispatch({ type: 'SCROLL_TO_WORKSPACE', payload: { workspaceId } });
  };

  const numWorkspaces = workspacesData?.account?.workspaces?.totalCount ?? 0;

  return (
    <WorkspacesNav
      accountTier={accountTier}
      canCreateWorkspaces={Boolean(canCreateWorkspaces)}
      error={error}
      fetchNext={handleFetchNext}
      loadingMoreWorkspaces={hasNextPage && workspacesLoading}
      numWorkspaces={numWorkspaces}
      onNavItemClick={onWorkspaceSelected}
      shouldRender={!loading}
      showInvitedToNavItem={invitedToProjectIds.length > 0}
      workspaceIds={workspaceIds}
    />
  );
};
